import type { ReactNode } from 'react';
import {
  StatementPositiveSolid,
  StatementNegativeSolid,
  StatementNoDataSolid,
} from '@simplywallst/ui-core/icons';
import {
  StatementWrapper,
  StatementAnchor,
  StatementTitle,
  Blur,
} from '@/components/Statement/styled';
import { useFlag } from '@/features/unleash';
import useUser from '@/hooks/useUser';
import type { Statement as CompanyStatement } from '@simplywallst/services/lib/endpoints/types/company/general/CompanyAPIStatements';

//TODO @ric: remove ReduxValue types once Redux statements is completely removed from the Report
type ReduxValue = 'Pass' | 'Fail' | 'No data' | 'UnInitialised';

export type StatementValue = ReduxValue | CompanyStatement['value'];

const getStatementIcon = (value?: StatementValue) => {
  switch (value) {
    case true:
    case 'Pass':
      return <StatementPositiveSolid data-cy-id="pass" />;
    case false:
    case 'Fail':
      return <StatementNegativeSolid data-cy-id="fail" />;
    case null:
    case 'No data':
      return <StatementNoDataSolid data-cy-id="no-data" />;
    default:
      return null;
  }
};

export interface Props {
  value?: StatementValue;
  title?: string;
  description?: ReactNode;
  statementId?: string;
  noIcon?: boolean;
  size?: 'small' | 'default';
  canBlur?: boolean;
}

const Statement = ({
  value,
  title,
  description,
  statementId,
  noIcon,
  size = 'default',
  // Only used with the inline rego modal experiment
  canBlur = true,
}: Props) => {
  const { data: userData } = useUser();
  const flag = useFlag({ name: 'mono-inline-rego' });
  const shouldEnableInlineRego =
    flag.enabled &&
    flag.flagsReady &&
    flag.variant.name === 'default-inline-rego';

  return (
    <StatementWrapper
      value={value}
      noIcon={noIcon}
      size={size}
      data-cy-id={`report-statement-${statementId}`}
    >
      <StatementAnchor id={statementId} />
      {!noIcon && getStatementIcon(value)}
      <p>
        {title && (
          <StatementTitle value={value}>
            {title}
            {description && ': '}
          </StatementTitle>
        )}
        {!userData.loggedIn &&
        shouldEnableInlineRego &&
        canBlur &&
        description ? (
          <Blur data-cy-id={`blur-report-statement-${statementId}`}>
            {description}
          </Blur>
        ) : (
          description
        )}
      </p>
    </StatementWrapper>
  );
};

export default Statement;
